import {
    Devis,
    FactureFormalite,
    useFacturationService,
    useSociete,
} from '@europrocurement/l2d-domain';
import {
    DevisFormalitApiApiDevisCollectionsPostRequest,
    DevisFormalitApiApiDevisFormaliteGetCollectionRequest,
    DevisFormalitDevisbyCollectionCreateDtoJsonldDevisCollectionCreate,
    DevisFormalitJsonldDevisReadPdf,
    FactureApiApiFacturesGetCollectionRequest,
    FactureApiApiFacturesIdAvoirPutRequest,
    FactureApiApiFacturesIdDeleteRequest,
    FactureApiApiFacturesIdValidatePutRequest,
    FactureJsonldFactureCreate,
    FactureJsonldFactureRead,
    FactureJsonldFactureReadPdf,
} from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import { CollectionDeDossierJsonldCollectiondossierRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { DossiersArticlesReductions } from './useDossierPrefacturation';

export type PdfInvoiceMetadata = FactureJsonldFactureReadPdf;
export type PdfEstimateMetadata = DevisFormalitJsonldDevisReadPdf;

export type useBillingProps = {
    origine?: 'L2DF' | 'B2D';
};
const useBilling = ({ origine = 'L2DF' }: useBillingProps) => {
    const xIdSociete = useSociete();
    const { devisFormaliteApi, factureApi } = useFacturationService();

    let getEstimatesTimeout: number | undefined;
    let getInvoicesTimeout: number | undefined;
    let createInvoiceTimeout: number | undefined;
    let validateInvoiceTimeout: number | undefined;
    let deleteInvoiceTimeout: number | undefined;
    let avoirInvoiceTimeout: number | undefined;

    const factureEnPreparation = origine === 'B2D' ? undefined : false;

    const getEstimates = (
        idCollections: number[],
        actif: boolean | undefined,
    ): Promise<Array<Devis>> =>
        new Promise((resolve, reject) => {
            clearTimeout(getEstimatesTimeout);
            const requestParameters: DevisFormalitApiApiDevisFormaliteGetCollectionRequest = {
                xIdSociete,
                collectionsId2: idCollections,
                actif,
            };

            getEstimatesTimeout = setTimeout(() => {
                devisFormaliteApi
                    .apiDevisFormaliteGetCollection(requestParameters)
                    .then((res) => resolve(res.data['hydra:member'] as Array<Devis>))
                    .catch(() => reject());
            }, 300);
        });

    const getInvoices = (idCollections: number[]): Promise<Array<FactureFormalite>> =>
        new Promise((resolve, reject) => {
            clearTimeout(getInvoicesTimeout);
            const requestParameters: FactureApiApiFacturesGetCollectionRequest = {
                xIdSociete,
                collectionsId2: idCollections,
                factureEnPreparation,
                orderDateEcheanceFacture: 'asc',
                orderFactureEnPreparation: 'asc',
                orderNumero: 'asc',
                orderPaye: 'asc',
            };

            getInvoicesTimeout = setTimeout(() => {
                factureApi
                    .apiFacturesGetCollection(requestParameters)
                    .then((res) => resolve(res.data['hydra:member'] as Array<FactureFormalite>))
                    .catch(() => reject());
            }, 300);
        });

    const getEstimateLabel = (estimate: Devis): string => `Devis ${estimate.numero}`;

    const getInvoiceLabel = (invoice: FactureFormalite): string => {
        const totalTtc = invoice.totalTtc as unknown as number;

        if (totalTtc < 0) {
            return `Avoir ${invoice.numero}`;
        }

        return `Facture ${invoice.numero}`;
    };

    const isInvoicePaid = (invoice: FactureFormalite): boolean =>
        invoice.paye ||
        (invoice.montantPaiementRecu !== undefined &&
            invoice.totalTtc !== undefined &&
            (Number(invoice.totalTtc) <= 0 ||
                Number(invoice.montantPaiementRecu) >= Number(invoice.totalTtc)));

    const getInvoiceRemainingAmount = (invoice: FactureFormalite): number => {
        const totalTtc = invoice.totalTtc as unknown as number;
        const alreadyPaid = invoice.montantPaiementRecu as unknown as number;

        if (totalTtc < 0) {
            return 0;
        }

        return totalTtc - alreadyPaid;
    };

    const downloadInvoice = (id: number): Promise<Blob> => {
        const resolver = async (resolve: (content: Blob) => void, reject: () => void) => {
            try {
                const requestParameters = { xIdSociete, id: id as unknown as string };

                const res = await factureApi.apiFacturesIdPdfGet(requestParameters, {
                    responseType: 'blob',
                });

                return resolve(res.data as unknown as Blob);
            } catch (err) {
                console.error(err);

                return reject();
            }
        };

        return new Promise(resolver);
    };

    const downloadEstimate = (id: number): Promise<Blob> => {
        const resolver = async (resolve: (content: Blob) => void, reject: () => void) => {
            try {
                const requestParameters = { xIdSociete, id: id as unknown as string };

                const res = await devisFormaliteApi.apiDevisFormaliteIdPdfGet(requestParameters, {
                    responseType: 'blob',
                });

                return resolve(res.data as unknown as Blob);
            } catch (err) {
                console.error(err);

                return reject();
            }
        };

        return new Promise(resolver);
    };

    const canUpdateBillingInfos = (invoices: Array<FactureFormalite>): boolean =>
        invoices.find((invoice) => ((invoice?.totalTtc || 0) as number) > 0) === undefined;

    const createInvoice = (
        prefacture: DossiersArticlesReductions,
        acompteIds: Array<string>,
    ): Promise<FactureJsonldFactureRead> =>
        new Promise((resolve, reject) => {
            clearTimeout(createInvoiceTimeout);
            const lignes =
                prefacture?.dossiers.flatMap(
                    (dossier) =>
                        dossier.articles
                            ?.filter((article) => article.articleFacturable)
                            .map((article) => ({ idFproPrestaFormArt: article.id })) ?? [],
                ) ?? [];

            if (!lignes || lignes.length === 0) {
                throw new Error('Aucun articles à facturer');
            }
            const factureJsonldFactureCreate: FactureJsonldFactureCreate = {
                lignes,
                reglements: acompteIds,
            };
            console.log(factureJsonldFactureCreate);

            const requestParameters = {
                factureJsonldFactureCreate,
                xIdSociete,
            };

            createInvoiceTimeout = setTimeout(() => {
                factureApi
                    .apiFacturesPost(requestParameters)
                    .then((res) => resolve(res.data))
                    .catch((error) => reject(error));
            }, 300);
        });

    const validateInvoice = (factureId: number): Promise<void> =>
        new Promise((resolve, reject) => {
            clearTimeout(validateInvoiceTimeout);

            const requestParameters: FactureApiApiFacturesIdValidatePutRequest = {
                id: factureId as unknown as string,
                factureJsonld: {},
                xIdSociete,
            };
            validateInvoiceTimeout = setTimeout(() => {
                factureApi
                    .apiFacturesIdValidatePut(requestParameters)
                    .then(() => resolve())
                    .catch((error) => reject(error));
            }, 300);
        });

    const deleteInvoice = (factureId: number): Promise<void> =>
        new Promise((resolve, reject) => {
            clearTimeout(deleteInvoiceTimeout);

            const requestParameters: FactureApiApiFacturesIdDeleteRequest = {
                id: factureId as unknown as string,
                factureJsonld: {},
                xIdSociete,
            };
            deleteInvoiceTimeout = setTimeout(() => {
                factureApi
                    .apiFacturesIdDelete(requestParameters)
                    .then((res) => resolve(res.data))
                    .catch((error) => reject(error));
            }, 300);
        });

    const avoirInvoice = (factureId: number): Promise<void> =>
        new Promise((resolve, reject) => {
            clearTimeout(avoirInvoiceTimeout);

            const requestParameters: FactureApiApiFacturesIdAvoirPutRequest = {
                id: factureId as unknown as string,
                factureJsonld: {},
                xIdSociete,
            };
            avoirInvoiceTimeout = setTimeout(() => {
                factureApi
                    .apiFacturesIdAvoirPut(requestParameters)
                    .then((res) => resolve(res.data))
                    .catch((error) => reject(error));
            }, 300);
        });

    const createEstimates = (
        idCollections: number[],
    ): Promise<CollectionDeDossierJsonldCollectiondossierRead> =>
        new Promise((resolve, reject) => {
            clearTimeout(createInvoiceTimeout);
            console.log(idCollections);

            const devisFormalitDevisbyCollectionCreateDtoJsonldDevisCollectionCreate: DevisFormalitDevisbyCollectionCreateDtoJsonldDevisCollectionCreate =
                {
                    ids: idCollections,
                };

            const requestParameters: DevisFormalitApiApiDevisCollectionsPostRequest = {
                devisFormalitDevisbyCollectionCreateDtoJsonldDevisCollectionCreate,
                xIdSociete,
            };
            createInvoiceTimeout = setTimeout(() => {
                devisFormaliteApi
                    .apiDevisCollectionsPost(requestParameters)
                    .then((res) => resolve(res.data))
                    .catch((error) => reject(error));
            }, 300);
        });

    return {
        getEstimates,
        getInvoices,
        getEstimateLabel,
        getInvoiceLabel,
        isInvoicePaid,
        getInvoiceRemainingAmount,
        downloadInvoice,
        downloadEstimate,
        canUpdateBillingInfos,
        createInvoice,
        createEstimates,
        validateInvoice,
        deleteInvoice,
        avoirInvoice,
    };
};

export default useBilling;
